import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import NavBar from "../../organisms/NavBar";
import { ThemeProvider } from "@material-ui/core/styles";
import customTheme from "./customTheme";
import Footer from "../../organisms/Footer";
import { AuthenticationRoutes } from "../../routes";
import { default as AccountActivationNavBar } from "@thrivetribe/gloji.ui.organisms.navbar";
import Logo from "@thrivetribe/gloji.assets.logo";
import { NAVY, MINT } from "@thrivetribe/gloji.enums.brand-colors";
import { useRouteMatch } from "react-router-dom";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./Layout.scss";
import { useDispatch } from "react-redux";
import useUserId from "../../hooks/useUserId";
import { profileApiV3 } from "../../../redux/profileApiV3";
import { CloseButton } from "../../../components/atoms/Toastify";
import { loseWeightCardData, smokeFreeCardData, Programmes } from "../../../data/Programme/Programmes";

const accountActivationPath = "/sign-up/activation/:key",
  eligiblityCheckerPath = "/sign-up/eligibility-checker",
  eligiblityCheckerPathLoseWeight = "/sign-up/eligibility-checker/lose-weight",
  eligiblityCheckerPathSmokeFree = "/sign-up/eligibility-checker/smoke-free",
  profileBuilderPath = "/sign-up/profile-builder/:programmeSlug",
  oldProfileBuilderPath = "/profile-builder/:programmeSlug",
  accountVerifyPath = "/account/verify-email",
  bookAppointmentPath = "/book-appointment/:programmeSlug",
  programmeStepPath = "/my-path/:programmeSlug/step-:stepNumber",
  myProgressPath = "/my-progress/:programmeSlug",
  otpPath = "/sign-up",
  selectProgrammePath = "/select-programme",
  homepagePath = "/";

const paths = [
  accountActivationPath,
  eligiblityCheckerPathLoseWeight,
  eligiblityCheckerPathSmokeFree,
  eligiblityCheckerPath,
  profileBuilderPath,
  oldProfileBuilderPath,
  accountVerifyPath,
  bookAppointmentPath,
  programmeStepPath,
  myProgressPath,
  otpPath,
  selectProgrammePath,
  homepagePath,
];

/**
 * The main layout of the app including NavBar, main body, and footer.
 * @param {ReactChildren} children - The child components passed to the layout component
 */

const defaultFlags = {
  hideMenu: false,
  showCloseButton: false,
  isSelectProgrammePage: false,
  isOtp: false,
  isActivation: false,
  programmeTitle: "",
};

function Layout({ children }) {
  const [hideMenu, setHideMenu] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [isSelectProgrammePage, setIsSelectProgrammePage] = useState(false);
  const [isOtp, setIsOtp] = useState(false);
  const [isActivation, setIsActivation] = useState(false);
  const [programmeTitle, setProgrammeTitle] = useState();

  const match = useRouteMatch({
    path: paths,
    exact: false,
    strict: false,
    sensitive: false,
  });

  const setFlags = flags => {
    setHideMenu(flags.hideMenu);
    setShowCloseButton(flags.showCloseButton);
    setIsSelectProgrammePage(flags.isSelectProgrammePage);
    setIsOtp(flags.isOtp);
    setIsActivation(flags.isActivation);
    setProgrammeTitle(flags.programmeTitle);
  };

  const dispatch = useDispatch();
  const [userId] = useUserId();

  useEffect(() => {
    const setMenuState = async () => {
      if (match) {
        switch (match.path) {
          case homepagePath:
            setFlags({ ...defaultFlags });
            break;
          case otpPath:
            setFlags({ ...defaultFlags, isOtp: true });
            break;
          case selectProgrammePath:
            setFlags({
              ...defaultFlags,
              isSelectProgrammePage: true,
              hideMenu: true,
              showCloseButton: true,
            });
            break;
          case accountActivationPath:
            setFlags({ ...defaultFlags, isActivation: true });
            break;
          case eligiblityCheckerPathSmokeFree:
            setFlags({
              ...defaultFlags,
              showCloseButton: true,
              hideMenu: true,
              programmeTitle: "Smoke free",
            });
            break;
          case eligiblityCheckerPath:
          case eligiblityCheckerPathLoseWeight:
            setFlags({
              ...defaultFlags,
              showCloseButton: true,
              hideMenu: true,
              programmeTitle: "Lose weight",
            });
            break;

          case profileBuilderPath:
          case oldProfileBuilderPath:
          case accountVerifyPath:
          case bookAppointmentPath:
            setFlags({ ...defaultFlags, hideMenu: true });
            break;
          case myProgressPath:
          case programmeStepPath: {
            const profileQuery = await dispatch(profileApiV3.endpoints.getProfileById.initiate(userId));

            if (profileQuery?.isSuccess && profileQuery?.data) {
              const currentProgramme = Programmes.find(x => x.slug === match.params?.programmeSlug);
              const currentJourney = profileQuery.data?.currentJourneys.find(
                x => x.programmeId === currentProgramme?.programmeId
              );

              if (currentJourney) {
                const myProgress = currentProgramme.features?.myProgress === true;
                const initialWeightLogged = currentJourney.flags?.initialWeightLogged === true;
                const step0Completed = currentJourney.flags?.step0Completed === true;
                if (match.path === programmeStepPath && !step0Completed) {
                  setFlags({ ...defaultFlags, hideMenu: true });
                }
                // if (match.path === myProgressPath && !initialWeightLogged && myProgress) {
                //     setFlags({ ...defaultFlags, hideMenu: true });
                //   }
                // Only perform program-specific checks if programmeId exists
                if (currentJourney.programmeId) {
                  const programmeId = currentJourney.programmeId;

                  if ([smokeFreeCardData.programmeId].includes(programmeId)) {
                    if (match.path === myProgressPath && !initialWeightLogged) {
                      setFlags({ ...defaultFlags, hideMenu: true });
                    }
                  }

                  if ([loseWeightCardData.programmeId].includes(programmeId)) {
                    if (match.path === myProgressPath && !initialWeightLogged && myProgress) {
                      setFlags({ ...defaultFlags, hideMenu: true });
                    }
                  }
                } else {
                  // programmeId is null or missing, set default flags
                  setFlags({ ...defaultFlags });
                }
              }
            }
            break;
          }

          default:
            break;
        }
      }
    };

    setMenuState();
  }, [dispatch, match, userId]);

  return (
    <ThemeProvider theme={customTheme}>
      <AuthenticationRoutes />
      {isActivation ? (
        <AccountActivationNavBar
          color={MINT}
          borderColor={NAVY}
          className={`${hideMenu ? "signup-flow-header" : null}`}
        >
          <div className={`header__logo-container nav-left-block header-item__container`}>
            <Logo
              url={`${hideMenu ? "https://www.gloji.co.uk" : "/"}`}
              id="logo-standard-navy"
              logoColor={NAVY}
              className={`header__logo${hideMenu ? " header__logo--center" : ""}`}
              title={"Go to home page"}
            />
          </div>
        </AccountActivationNavBar>
      ) : isOtp ? (
        <></>
      ) : (
        <NavBar hideMenu={hideMenu} showCloseButton={showCloseButton} programmeTitle={programmeTitle} />
      )}
      <main role="main">{children}</main>
      {hideMenu && !isSelectProgrammePage ? null : <Footer />}
      <ToastContainer
        hideProgressBar={true}
        autoClose={3500}
        position="bottom-right"
        transition={Slide}
        closeButton={CloseButton}
      />
    </ThemeProvider>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
};

export default Layout;
